import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { cognitoHostedUIBase } from '../authConfig';
import { talktreesCleanupFederatedUrl } from '../apiConfig';
import { setAuthToken, setTokenExpiry, setLoggedInUser, setLoggedInUserName } from '../utils/auth';

const defaultTheme = createTheme();
const REQUIRED_GROUP = 'talktrees_instructors';

function parseHashFragment() {
  const hash = window.location.hash?.replace(/^#/, '') || '';
  const params = new URLSearchParams(hash);
  return {
    id_token: params.get('id_token'),
    access_token: params.get('access_token'),
    state: params.get('state'),
    error: params.get('error'),
    error_description: params.get('error_description'),
  };
}

function parseJwtPayload(token) {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getDisplayNameFromPayload(payload) {
  if (payload.given_name) return payload.given_name;
  if (payload.name) return payload.name.split(' ')[0];
  if (payload['cognito:username']) return payload['cognito:username'];
  return '';
}

export default function AuthCallback() {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }
    hasProcessedCallback.current = true;

    const { id_token, access_token, error, error_description } = parseHashFragment();

    if (error) {
      setStatus('error');
      const baseMessage =
        error_description || error || 'Sign-in was cancelled or failed.';
      const hasInviteHint = /registration code|registrationCode|invite/i.test(baseMessage);
      setMessage(
        hasInviteHint
          ? baseMessage
          : `${baseMessage} If you need an account, use Sign up and your instructor registration code first.`
      );
      mixpanel.track('loginFailed', { method: 'Google', error: error_description || error });
      return;
    }

    const token = id_token || access_token;
    if (!token) {
      setStatus('error');
      setMessage('No token returned. Try signing in again.');
      return;
    }

    const payload = parseJwtPayload(token);
    if (!payload) {
      setStatus('error');
      setMessage('Invalid token from sign-in.');
      return;
    }

    const groups = payload['cognito:groups'] || [];
    if (!groups.includes(REQUIRED_GROUP)) {
      // This Google account was provisioned by Cognito during the login attempt
      // but never completed sign-up (no group). We delete the orphan
      // shadow user so it doesn't linger or collide with a later native sign-up
      // of the same email. Fire-and-forget: never blocks or changes the UX.
      fetch(talktreesCleanupFederatedUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: token }),
      }).catch(() => {
        // no-op: cleanup is best-effort
      });

      setStatus('error');
      setMessage(
        'You do not have access to Talk Trees using Log in with Google. Please complete the Sign Up process or Log in using your email address.');
      mixpanel.track('loginFailed', { method: 'Google', error: 'User not in required group' });
      return;
    }

    const exp = payload.exp ? payload.exp * 1000 : Date.now() + 3600000;
    const loggedInUser = payload.email || payload['cognito:username'] || '';
    setAuthToken(token);
    setTokenExpiry(String(exp));
    setLoggedInUser(loggedInUser);
    setLoggedInUserName(getDisplayNameFromPayload(payload));

    /* global mixpanel */
    mixpanel.identify(loggedInUser);
    mixpanel.people.set({ lastLoginMethod: 'Google', role: 'tutor' });
    mixpanel.register({ loginMethod: 'Google' });
    mixpanel.track('userLoggedIn', { method: 'Google', username: loggedInUser });

    window.history.replaceState(null, '', window.location.pathname);
    setStatus('success');
    window.location.replace('/Dashboard');
  }, []);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Signing you in…</Typography>
            </>
          )}
          {status === 'error' && (
            <>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {message}
              </Alert>
              <Button component={RouterLink} to="/login" variant="contained" sx={{ mr: 1 }}>
                Back to Log in
              </Button>
              <Button component={RouterLink} to="/signup" variant="outlined">
                Sign up
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary' }}>
                Hosted UI domain: {cognitoHostedUIBase}
              </Typography>
            </>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
